import { observable } from 'rewire-core';
import { Form, IFormContext } from 'rewire-ui';
import { ISettings, ISettingsBase, ISettingsGroup, ISettingsOptionsMenu, settingsGroup, settings } from './SettingsModel';
import { settingsDialogModel } from './SettingsDialog';
import DataMutations, { archiveUnArchive } from '../graphql/Mutations';
import { MasterDataSettingsModel } from './MasterDataSettingsModel';
import udtsSubscriptionService from '../services/UdtsSubscriptionService';
import DocumentDialogModel, { DocumentSchema } from '../schedule/DocumentDialogModel';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import EditIcon from '@material-ui/icons/EditOutlined';
import { notifications } from '../ui/Notifications';
import { IUdt, ISubscriptionRecord, UdfDocumentType, ISort } from '../DataTypes';
import mainPageModel from '../main-navigation/MainPageModel';
import AreasSettingsModel from './AreasSettingsModel';
import EmployeeSettingsModel from './EmployeeSettingsModel';
import TimecardExporterRuleEditorModel from './TimecardExporterRuleEditorModel';
import AutoScheduleRuleEditorModel from './AutoSchedulerRuleEditorModel';
import TimeOffAccrualRuleEditorModel from './TimeOffAccrualRuleEditorModel';
import GeneratorsSettingsModel from './GeneratorsSettingsModel';
import GroupsSettingsModel from './GroupsSettingsModel';
import LocationsSettingsModel from './LocationsSettingsModel';
import OccupationsSettingsModel from './OccupationsSettingsModel';
import ReasonsSettingsModel from './ReasonsSettingsModel';
import RuleSettingsEditorModel from './RuleSettingsEditorModel';
import APISettingsModel from './APISettingsModel';
import AuthenticationSettingsModel from './AuthenticationSettingsModel';
import UsersSettingsModel from './UsersSettingsModel';
import RolesSettingsModel from './RolesSettingsModel';
import BillingsSettingsModel from './BillingsSettingsModel';
// import AutomationRuleEditorModel       from './AutomationRuleEditorModel';
// import TriggersRuleEditorModel         from './TriggersRuleEditorModel';
import TemplatesSettingsModel from './TemplatesSettingsModel';
import TimeOffTypesSettingsModel from './TimeOffTypesSettingsModel';
import ValidationRuleEditorModel from './ValidationRuleEditorModel';
import UserDefinedTypesSettingsPanel from './UserDefinedTypesSettingsPanel';
import DataQueries from '../graphql/Queries';

interface ISettingsPanelModelState {
  udtSettingsInitialized: boolean;
  settingsPanelRefreshToggle: boolean;
}

class SettingsPanelModel {
  state: ISettingsPanelModelState;
  private _settings: ISettingsBase[];
  private _udtParent: string = 'Manage';

  public constructor() {
    this.state = observable({
      udtSettingsInitialized: false,
      settingsPanelRefreshToggle: false,
    });

    this._settings = [
      settingsGroup(
        'Manage',
        [
          settings('Employees', EmployeeSettingsModel),
          settingsGroup('Organization', [
            settings('Location', LocationsSettingsModel),
            settings('Area', AreasSettingsModel),
            settings('Group', GroupsSettingsModel),
            settings('Occupation', OccupationsSettingsModel),
          ]),
          settings('Shift Pattern', GeneratorsSettingsModel),
          settings('Shift Type', TemplatesSettingsModel),
          settings('Reason', ReasonsSettingsModel),
          settings('Time Off Type', TimeOffTypesSettingsModel),
        ],
        true,
        { title: 'Add User Defined Type', onClick: this.handleAddUserDefinedType, visible: () => mainPageModel.canAddUdts },
      ),
      // settingsGroup('Channels', [
      //   settings('Email'),
      //   settings('Text'),
      //   settings('API'),
      // ]),
      settingsGroup('Business Rules', [
        // settings('Triggers', TriggersRuleEditorModel),
        // settings('Automations', AutomationRuleEditorModel),
        settings('Timecard', RuleSettingsEditorModel),
        settings('Auto Schedule', AutoScheduleRuleEditorModel),
        settings('Export', TimecardExporterRuleEditorModel),
        settings('Time Off Accruals', TimeOffAccrualRuleEditorModel),
        settings('Validations', ValidationRuleEditorModel),
      ]),
      settingsGroup('Settings', [
        settings('Authentication', AuthenticationSettingsModel),
        settings('API', APISettingsModel),
        settings('Users', UsersSettingsModel),
        settings('Roles', RolesSettingsModel),
        settings('Billing', BillingsSettingsModel),
        // settings('Security'),
      ]),
    ];
  }

  load() {
    if (!this.udtSettingsInitialized) {
      this._initializeUdtSettings();
    }
  }

  _initializeUdtSettings() {
    this.state.udtSettingsInitialized = true;
    const udts = udtsSubscriptionService.subscriptionModel.udts.map((r) => this.createUdtSettings(r));
    for (const udt of udts) {
      this.addUdtSettings(udt as unknown as ISettings);
    }
    udtsSubscriptionService.subscriptionModel.setSubscriptionCallback(this.udtSubscriptionServiceCallback);
  }

  udtSubscriptionServiceCallback = (udtRecord: ISubscriptionRecord<IUdt>) => {
    const udt = udtRecord['udt'] as IUdt;
    if (udtRecord.operation === 'deleted') {
      this.removeUdtSettings(udt.displayName);
    } else {
      const udtSettingsList = (this.settings.find((s) => s.title === this._udtParent) as ISettingsGroup)?.group;
      if (!udtSettingsList) return;
      const currentUdtSettings = udtSettingsList.find((s) => s.id === udt.id);
      if (currentUdtSettings) {
        // updated
        const newUdtSettings = this.createUdtSettings(udt);
        this.updateUdtSettings(currentUdtSettings.title, newUdtSettings);
      } else {
        // added
        const newUdtSettings = this.createUdtSettings(udt);
        this.addUdtSettings(newUdtSettings);
      }
    }

    this.refreshSettingsPanel();
  };

  get udtSettingsInitialized() {
    return this.state.udtSettingsInitialized;
  }

  get settingsPanelRefreshToggle() {
    return this.state.settingsPanelRefreshToggle;
  }

  get settings(): ISettingsBase[] {
    return this._settings;
  }

  refreshSettingsPanel() {
    this.state.settingsPanelRefreshToggle = !this.settingsPanelRefreshToggle;
  }

  async handleAddUserDefinedType() {
    const successForm = Form.create(
      (form: IFormContext) => ({
        name: form
          .string()
          .label('Display Name')
          .validators('required')
          .placeholder('enter the name of your user defined type')
          .autoFocus(),
      }),
      { successMessage: '' },
      { validateOnUpdate: true, updateOnChange: true },
    );
    const result = await settingsDialogModel.load({
      title: `Add User Defined Type`,
      form: successForm,
      buttons: ['cancel', 'save'],
    });
    if (result === 'save') {
      const displayName = successForm.field.name.value;
      const result = await DataMutations.insertUserDefinedTypes('id', [{ displayName }]);
      if (result) {
        notifications.success('User Defined Type Added');
      } else {
        notifications.error('Adding User Defined Type Failed');
      }
    }
  }

  async handleRenameUdt(udt: IUdt) {
    const successForm = Form.create(
      (form: IFormContext) => ({
        name: form
          .string()
          .label('User Defined Type')
          .validators('required')
          .placeholder('enter the name of your user defined type')
          .autoFocus(),
      }),
      { name: udt.displayName, successMessage: '' },
      { validateOnUpdate: true, updateOnChange: true },
    );
    const result = await settingsDialogModel.load({
      title: `Rename User Defined Type`,
      form: successForm,
      buttons: ['cancel', 'save'],
    });
    if (result === 'save') {
      const displayName = successForm.field.name.value;
      const result = await DataMutations.updateUserDefinedTypes('id', [{ id: udt.id, displayName }]);
      if (result) {
        notifications.success('User Defined Type Renamed');
      } else {
        notifications.error('Renaming User Defined Type Failed');
      }
    }
  }

  async handleDeleteUdt(udtId: string) {
    const result = await DataMutations.deleteUserDefinedTypes([udtId]);
    if (result) {
      notifications.success('User Defined Type Deleted');
    } else {
      notifications.error('User Defined Type Deletion Failed');
    }
  }

  createUdtSettings(udt: IUdt): ISettings {
    const documentsDialogModel = new DocumentDialogModel(
      udt.displayName,
      (ids: string[]) => DataQueries.getDocumentByIds(DocumentSchema, ids),
      DataMutations.insertDocuments.bind(this, udt.name),
      DataMutations.updateDocuments.bind(this, udt.name),
      DataMutations.deleteDocuments,
      udt.name as unknown as UdfDocumentType,
      () => mainPageModel.canAddUdts,
      () => mainPageModel.canEditUdts,
      () => mainPageModel.canDeleteUdts,
    );

    const documentsOptionsMenu: ISettingsOptionsMenu = {
      title: `${udt.displayName} Options`,
      menuItems: [
        {
          name: 'renameUserDefinedType',
          title: 'Rename User Defined Type',
          icon: EditIcon,
          onClick: () => this.handleRenameUdt(udt),
          closeOnClick: true,
          disabled: () => !mainPageModel.canEditUdts,
        },
        {
          name: 'deleteUserDefinedType',
          title: 'Delete User Defined Type',
          icon: DeleteIcon,
          onClick: () => this.handleDeleteUdt(udt.id),
          closeOnClick: true,
          disabled: () => !mainPageModel.canDeleteUdts,
        },
      ],
    };

    class DocumentsSettingsModel extends MasterDataSettingsModel {
      constructor(_: string) {
        super(udt.displayName, udt.name as unknown as UdfDocumentType, documentsDialogModel, undefined, documentsOptionsMenu);
        this.SettingsView = UserDefinedTypesSettingsPanel;
      }

      get _canAddItem(): boolean {
        return mainPageModel.canAddUdts;
      }

      get _canEditItem(): boolean {
        return mainPageModel.canEditUdts;
      }

      get _canDeleteItem(): boolean {
        return mainPageModel.canDeleteUdts;
      }

      protected async _load(filter: { search?: string }, sort?: ISort) {
        const results = await DataQueries.getDocuments(
          udt.name,
          DocumentSchema,
          filter,
          sort,
          this.paging.itemsPerPage,
          this.paging.currentPage,
        );
        this.setItems(results.data);
        return results;
      }

      async run(fn: 'archive' | 'unArchive') {
        await archiveUnArchive(fn, 'documents', this.selectedItem);
        this.paging.load(true);
      }
    }

    const newUdtSettings = settings(udt.displayName, DocumentsSettingsModel);
    newUdtSettings.id = udt.id;
    return newUdtSettings;
  }

  addSettings(parent: string, settings: ISettings, position?: number) {
    const group = this.settings.find((s) => s.title === parent) as ISettingsGroup;
    if (!group) return;
    (settings as ISettingsBase).level = () => group.level() + 1;
    group.group.splice(position ?? group.group.length, 0, settings);
  }

  updateSettings(parent: string, settingsTitle: string, newSettings: ISettings) {
    const group = this.settings.find((s) => s.title === parent) as ISettingsGroup;
    if (!group) return;
    const idx = group.group.findIndex((s) => s.title === settingsTitle);
    if (idx >= 0) {
      (newSettings as ISettingsBase).level = () => group.level() + 1;
      // two steps needed for proper refresh.
      group.group.splice(idx, 1);
      group.group.splice(idx, 0, newSettings);
    }
  }

  removeSettings(parent: string, settingsTitle: string) {
    const group = this.settings.find((s) => s.title === parent) as ISettingsGroup;
    if (!group) return;
    const idx = group.group.findIndex((s) => s.title === settingsTitle);
    if (idx >= 0) {
      group.group.splice(idx, 1);
    }
  }

  addUdtSettings(settings: ISettings) {
    this.addSettings(this._udtParent, settings);
  }

  updateUdtSettings(settingsTitle: string, newSettings: ISettings) {
    this.updateSettings(this._udtParent, settingsTitle, newSettings);
  }

  removeUdtSettings(settingsTitle: string) {
    this.removeSettings(this._udtParent, settingsTitle);
  }
}

const settingsPanelModel = new SettingsPanelModel();
export default settingsPanelModel;
